import { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  TextField,
  FormHelperText,
} from '@mui/material';
import { useEffectAsync } from '../../reactHelper';
import fetchOrThrow from '../util/fetchOrThrow';
import { usePremiumTokens } from '../theme/usePremiumTokens';

const SelectField = ({
  label,
  fullWidth,
  multiple,
  value = null,
  emptyValue = null,
  emptyTitle = '',
  onChange,
  endpoint,
  data,
  keyGetter = (item) => item.id,
  titleGetter = (item) => item.name,
  helperText,
  disabled,
}) => {
  const tokens = usePremiumTokens();
  const [items, setItems] = useState();

  // Safe token access with fallbacks
  const safeTokens = {
    surface: {
      elevated: tokens?.surface?.elevated || '#161b22',
      glass: tokens?.surface?.glass || 'rgba(255, 255, 255, 0.03)',
    },
    radius: {
      md: tokens?.radius?.md || '8px',
      lg: tokens?.radius?.lg || '12px',
    },
    transitions: {
      all: tokens?.transitions?.all || 'all 0.2s ease',
      colors: tokens?.transitions?.colors || 'color 0.2s ease, border-color 0.2s ease',
    },
    border: {
      default: tokens?.border?.default || 'rgba(255, 255, 255, 0.08)',
      subtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
    },
    accent: {
      primary: tokens?.accent?.primary || '#00d4aa',
      subtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
      hover: tokens?.accent?.hover || 'rgba(0, 212, 170, 0.15)',
    },
    text: {
      primary: tokens?.text?.primary || '#f0f6fc',
      secondary: tokens?.text?.secondary || '#8b949e',
      muted: tokens?.text?.muted || '#6e7681',
    },
    spacing: {
      sm: tokens?.spacing?.[2] || '0.5rem',
    },
  };

  const getOptionLabel = (option) => {
    if (typeof option !== 'object') {
      option = items.find((obj) => keyGetter(obj) === option);
    }
    return option ? titleGetter(option) : emptyTitle;
  };

  useEffect(() => setItems(data), [data]);

  useEffectAsync(async () => {
    if (endpoint) {
      const response = await fetchOrThrow(endpoint);
      setItems(await response.json());
    }
  }, []);

  // Premium dropdown menu styles
  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: 'rgba(22, 27, 34, 0.75)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRadius: safeTokens.radius.lg,
        border: `1px solid ${safeTokens.border.subtle}`,
        mt: 0.5,
        maxHeight: 300,
        '& .MuiMenuItem-root': {
          color: safeTokens.text.primary,
          padding: '10px 16px',
          transition: safeTokens.transitions.colors,
          '&:hover': {
            backgroundColor: safeTokens.accent.subtle,
          },
          '&.Mui-selected': {
            backgroundColor: safeTokens.accent.hover,
            '&:hover': {
              backgroundColor: safeTokens.accent.hover,
            },
          },
        },
      },
    },
  };

  // Premium autocomplete dropdown styles
  const autocompleteProps = {
    paper: {
      sx: {
        backgroundColor: 'rgba(22, 27, 34, 0.75)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRadius: safeTokens.radius.lg,
        border: `1px solid ${safeTokens.border.subtle}`,
        mt: 0.5,
        '& .MuiAutocomplete-listbox': {
          maxHeight: 300,
          '& .MuiAutocomplete-option': {
            color: safeTokens.text.primary,
            padding: '10px 16px',
            transition: safeTokens.transitions.colors,
            '&:hover': {
              backgroundColor: safeTokens.accent.subtle,
            },
            '&[aria-selected="true"]': {
              backgroundColor: safeTokens.accent.hover,
              '&:hover': {
                backgroundColor: safeTokens.accent.hover,
              },
            },
          },
        },
        '& .MuiAutocomplete-noOptions': {
          color: safeTokens.text.muted,
          padding: '14px 16px',
        },
        '& .MuiAutocomplete-loading': {
          color: safeTokens.text.muted,
          padding: '14px 16px',
        },
      },
    },
  };

  if (items) {
    return (
      <FormControl fullWidth={fullWidth} size="small" className="premium-input" disabled={disabled}>
        {multiple ? (
          <>
            <InputLabel>{label}</InputLabel>
            <Select
              label={label}
              multiple
              value={value}
              onChange={onChange}
              disabled={disabled}
              MenuProps={menuProps}
              sx={{
                '& .MuiSelect-icon': {
                  color: safeTokens.text.secondary,
                },
              }}
            >
              {items.map((item) => (
                <MenuItem key={keyGetter(item)} value={keyGetter(item)}>
                  {titleGetter(item)}
                </MenuItem>
              ))}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </>
        ) : (
          <Autocomplete
            size="small"
            options={items}
            getOptionLabel={getOptionLabel}
            disabled={disabled}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              // Sempre usar keyGetter(option) que retorna item.id para garantir unicidade
              const uniqueKey = keyGetter(option);
              return (
                <MenuItem
                  {...rest}
                  key={uniqueKey}
                  value={uniqueKey}
                  sx={{
                    color: safeTokens.text.primary,
                    '&:hover': {
                      backgroundColor: safeTokens.accent.subtle,
                    },
                  }}
                >
                  {titleGetter(option)}
                </MenuItem>
              );
            }}
            isOptionEqualToValue={(option, value) => keyGetter(option) === value}
            value={value}
            onChange={(_, value) => onChange({ target: { value: value ? keyGetter(value) : emptyValue } })}
            componentsProps={autocompleteProps}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                helperText={helperText}
                className="premium-input"
              />
            )}
          />
        )}
      </FormControl>
    );
  }
  return null;
};

export default SelectField;
